import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import { calculatorIcon, arrowLeft, Loader } from "../../../assets/images";
import { ChatService } from "../../../services/chat/chat.services";
import MathExpressionNew from "../../../components/common/mathjax";
import { scopeCSS } from "../../../utils/scopeCSS";
import "./StudentExercisePreviewModal.scss";

export interface StudentExercisePreviewModalProps {
  open: boolean;
  onClose: () => void;
  exerciseId: number | null;
}

export const StudentExercisePreviewModal: React.FC<StudentExercisePreviewModalProps> = ({
  open,
  onClose,
  exerciseId,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [exerciseContent, setExerciseContent] = useState<string>("");

  useEffect(() => {
    let isMounted = true;

    if (open && exerciseId) {
      setLoading(true);
      const chatService = new ChatService();

      chatService
        .GETEXERCISECONTENT(exerciseId)
        .then(async (res: any) => {
          if (!isMounted) return;
          const rawContent = res?.exercise_content || "";
          try {
            const scoped = await scopeCSS(rawContent);
            if (isMounted) setExerciseContent(scoped);
          } catch {
            if (isMounted) setExerciseContent(rawContent);
          }
        })
        .catch((err: any) => {
          console.error("Failed to load exercise content for preview:", err);
          if (isMounted) setExerciseContent("");
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    } else {
      setExerciseContent("");
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [open, exerciseId]);

  // Support Escape key to close the drawer
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose]);

  return (
    <div
      className={`student-exercise-drawer-overlay ${open ? "open" : ""}`}
      onClick={onClose}
      data-testid="student-exercise-drawer-overlay"
    >
      <div
        className="student-exercise-drawer-container"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        data-testid="student-exercise-drawer-container"
      >
        {/* Header matching exercise-practice drawer */}
        <div className="drawer-header">
          <div className="drawer-title-container">
            <img
              src={calculatorIcon}
              alt="Calculator"
              className="calculator-header-icon"
            />
            <h3 className="drawer-title">{t("exercise")}</h3>
          </div>
          <IconButton
            onClick={onClose}
            className="close-btn"
            aria-label={t("close")}
            data-testid="student-exercise-drawer-close-btn"
          >
            <img src={arrowLeft} alt="Close" draggable="false" />
          </IconButton>
        </div>

        {/* Body matching exercise-practice drawer */}
        <div className="drawer-body">
          {loading ? (
            <div className="loader-container">
              <img src={Loader} alt="Loading" className="spinner-img" />
            </div>
          ) : exerciseContent ? (
            <div className="exercise-text-content" id="scoped-db-content">
              <MathExpressionNew content={exerciseContent} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default StudentExercisePreviewModal;
